import React, { useCallback, useEffect, useRef } from "react";
import { Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../lib/confirm";
import { kirliFormSayisi, kirliTaslaklariYaz } from "../lib/dirtyForms";
import {
  getDetachedPanels,
  closeAllDetachedPanels,
  openDetachedPanelsBatch,
} from "../lib/detachedPanels";
import { currentViewport, fitGeom, readPanelGeom } from "../lib/windowLayouts";
import {
  registerWindowCollector,
  tumPencereleriGeriAc,
  tumPencereleriTopla,
  useYuzenPencereSayisi,
} from "../lib/windowCollectors";

// Görevler paneli kapalıyken (Kolay/Aydınlık/Pano vb.) iş kolu pencerelerini
// sayan/kapatan yedek toplayıcı. Ana panel mount ise devre dışı kalır.
const kategoriYedegi = {
  yedek: true,
  say: () => getDetachedPanels().filter((p) => p.category).length,
  kapat: () => {
    const kollar = getDetachedPanels().filter((p) => p.category);
    if (!kollar.length) return { sayi: 0 };
    const vp = currentViewport();
    const snap = {
      sayi: kollar.length,
      viewport: vp,
      kollar: kollar.map((p) => ({
        category: p.category,
        categoryName: p.categoryName || null,
        geom: readPanelGeom(p.id) || { x: 140, y: 90, width: 460, height: 620 },
      })),
    };
    closeAllDetachedPanels();
    return snap;
  },
  geriAc: (s) => {
    if (!s?.kollar?.length) return;
    const vp = currentViewport();
    openDetachedPanelsBatch(
      s.kollar.map((k) => ({ ...k, geom: fitGeom(k.geom, s.viewport || vp, vp) })),
    );
  },
};

// Tek dokunuşla TÜM yüzen pencereleri panele geri toplar. Pencere yokken
// düğme hiç görünmez. Toplama sonrası ~12 sn "GERİ AL" imkânı sunulur.
export const CollectAllButton = ({ light = false }) => {
  const sayi = useYuzenPencereSayisi();
  const kilitRef = useRef(false); // çift gönderim kilidi

  useEffect(() => registerWindowCollector("kategori-yedek", kategoriYedegi), []);

  const topla = useCallback(async () => {
    if (kilitRef.current) return;
    kilitRef.current = true;
    try {
      const kirli = kirliFormSayisi();
      if (kirli > 0) {
        const ok = await confirmDialog({
          title: "Kaydedilmemiş Bilgiler",
          message: `${kirli} pencerede kaydedilmemiş bilgi var, devam edilsin mi?\nDevam ederseniz bilgiler taslak olarak saklanır.`,
          confirmText: "Devam Et",
        });
        if (!ok) return;
        kirliTaslaklariYaz();
      }
      const snap = tumPencereleriTopla();
      const n = snap.reduce((a, x) => a + (x.s?.sayi || 0), 0);
      if (!n) return;
      const geriAlKilit = { kullanildi: false };
      toast(`${n} pencere toplandı`, {
        duration: 12000,
        action: {
          label: "GERİ AL",
          onClick: async () => {
            if (geriAlKilit.kullanildi) return; // idempotent
            geriAlKilit.kullanildi = true;
            await tumPencereleriGeriAc(snap);
          },
        },
      });
    } finally {
      kilitRef.current = false;
    }
  }, []);

  if (sayi < 1) return null;

  return (
    <button
      onClick={topla}
      data-testid="collect-all-btn"
      title="Tüm yüzen pencereleri panele geri topla"
      className={`px-2 py-1 rounded-md border hud-text text-[10px] flex items-center gap-1 shrink-0 whitespace-nowrap transition-colors ${
        light
          ? "border-sky-600/50 text-sky-700 bg-sky-50 hover:bg-sky-100"
          : "border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15"
      }`}
    >
      <Minimize2 className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">HEPSİNİ TOPLA</span> ({sayi})
    </button>
  );
};

export default CollectAllButton;
